import { ChangeEvent, memo } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';
import { IoCloseSharp } from 'react-icons/io5';

import theme from '@styles/theme';

type StyledFileUploadProps = {
    PREVIEW?: boolean;
    width?: number;
    margin?: string;
    previewWidth?: number;
    previewHeight?: number;
    title?: string;
    subTitle?: string;
    file?: File | null;
    previewUrl?: string;
    multiple?: boolean;
    accept?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onDelete?: () => void;
    placeholder?: string;
    description?: string;
};

const StyledFileUpload = ({
    PREVIEW = true,
    width,
    margin,
    previewWidth,
    previewHeight,
    title,
    subTitle,
    file,
    previewUrl,
    multiple = false,
    accept = 'image/*',
    onChange = () => null,
    onDelete = () => null,
    placeholder = '파일 첨부하기',
    description = '최적화 : 가로 102px * 세로 102px'
}: StyledFileUploadProps) => {
    return (
        <Wrapper $width={width} $margin={margin}>
            {title && (
                <Title>
                    {title}
                    {subTitle && <SubTitle>({subTitle})</SubTitle>}
                </Title>
            )}
            <InputWrapper>
                <InputBox>
                    <Placeholder>
                        {file?.name ? file.name : placeholder}
                    </Placeholder>
                    <Input
                        type="file"
                        multiple={multiple}
                        accept={accept}
                        onChange={onChange}
                    />
                </InputBox>
                {PREVIEW && (
                    <PreviewBox>
                        <ImageBox $width={previewWidth} $height={previewHeight}>
                            {previewUrl ? (
                                <>
                                    <Image src={previewUrl} key={previewUrl} />
                                    <DeleteButton onClick={onDelete}>
                                        <IoCloseSharp
                                            size={16}
                                            color={theme.colors.whiteColor}
                                        />
                                    </DeleteButton>
                                </>
                            ) : (
                                '미리보기'
                            )}
                        </ImageBox>
                        <Description>{description}</Description>
                    </PreviewBox>
                )}
            </InputWrapper>
        </Wrapper>
    );
};

export default memo(StyledFileUpload);

const Wrapper = styled.div<{ $width?: number; $margin?: string }>`
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
`;
const Title = styled.div`
    font-size: 14px;
    display: flex;
    align-content: center;
    margin-bottom: 8px;
`;
const SubTitle = styled.div`
    font-size: 14px;
    color: ${theme.colors.lightGrayFontColor};
    margin-left: 4px;
`;
const InputWrapper = styled.div`
    display: block;
    margin-top: 8px;
`;
const InputBox = styled.label`
    width: 100%;
    height: 50px;
    padding: 12px;
    display: flex;
    align-items: center;
    border: 1px solid ${theme.colors.lightGrayBorder};
    border-radius: 4px;
    cursor: pointer;
`;
const Placeholder = styled.div`
    font-size: 14px;
    color: ${theme.colors.deepGrayFontColor};
`;
const Input = styled.input`
    display: none;
`;
const PreviewBox = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
`;
const ImageBox = styled.div<{ $width?: number; $height?: number }>`
    position: relative;
    width: ${({ $width }) => ($width ? $width : 144)}px;
    height: ${({ $height }) => ($height ? $height : 144)}px;
    font-size: 14px;
    color: #979797;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    border-radius: 4px;
    background-color: ${theme.colors.ultraLightGrayBorder};
    overflow: hidden;
`;
const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;
const Description = styled.div`
    font-size: 14px;
    color: ${theme.colors.deepGrayFontColor};
    line-height: 1.2;
    margin-left: 8px;
`;
const DeleteButton = styled.button`
    position: absolute;
    top: 6px;
    right: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background-color: ${theme.colors.lightGrayBorder};
    border-radius: 50%;
    transition: all 0.2s;
    &:hover {
        background-color: ${darken(0.05, theme.colors.lightGrayBorder)};
    }
`;
